import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import ThemedChartTooltip from '../../shared/ThemedChartTooltip';
import {
  getAimRuns, getAimQueueHealth, getAimCostSummary, getAimErrors, getAimDataCounts,
} from '../../../api/auth';
import AimEmpty, { AimLoading } from '../primitives/AimEmpty';
import InfoTooltip from '../primitives/InfoTooltip';
import { fmtDuration, isAbsurdDuration } from '../lib/duration';
import usePagedRows from '../lib/usePagedRows';
import AimPagination from '../primitives/AimPagination';
import { prettifyCategory } from '../lib/categoryLabel';
import { queueHealthColor } from '../lib/queueHealth';
import { colorFor, prettifyProviderLabel } from '../lib/providerColors';
import { buildCostSeries, limitCostRuns } from '../lib/costSummary';
import { useView } from '../../../contexts/ViewContext';
import { TABLE_FRAME_FLUSH, TABLE_THEAD, TABLE_ROW, TABLE_ROW_CLICKABLE, clickableRowProps } from '../../shared/tableStyles';

function fmtCost(n) { return n == null ? '—' : `$${Number(n).toFixed(2)}`; }

function fmtRunDate(raw) {
  if (!raw) return '—';
  const d = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function RunOverview({ clientId, onSelectRun }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    if (!clientId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    // Sourced from fn_get_aim_runs, not fn_get_aim_system_runs. The system_runs
    // RPC dropped run_date, total_api_calls and duration_seconds — three of the
    // six columns below — which left them blank and silently disabled the
    // date-range filter (`r.run_date` was always undefined, so every row
    // passed). fn_get_aim_runs returns the identical 26-run set for this client
    // (verified against the query in the 2026-07-27 parity capture) and carries
    // every field this table renders. Revert once fn_get_aim_system_runs is
    // restored — see backend parity item #6.
    getAimRuns(clientId)
      .then((resp) => { if (!cancelled) setRows(resp?.data || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  if (loading) return <AimLoading height={160} />;
  if (rows.length === 0) return <AimEmpty message="No runs yet." />;
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Run Overview (8A)</h3>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
          Recent AI Monitor runs in time order. &ldquo;Weekly Priority&rdquo; runs check core queries every week; &ldquo;Full&rdquo; runs sweep every query monthly; &ldquo;Manual&rdquo; runs are one-off triggers.
        </p>
      </div>
      <div className={`${TABLE_FRAME_FLUSH} responsive-card-table-wrapper`}>
      <table className="w-full text-sm text-gray-900 dark:text-gray-100 min-w-[720px] responsive-card-table">
        <thead className={TABLE_THEAD}>
          <tr>
            <th className="text-left px-4 py-2.5 whitespace-nowrap">Date</th>
            <th className="text-left px-4 py-2.5 whitespace-nowrap">Type</th>
            <th className="text-left px-4 py-2.5 whitespace-nowrap">Status</th>
            <th className="text-right px-4 py-2.5 whitespace-nowrap">Calls</th>
            <th className="text-right px-4 py-2.5 whitespace-nowrap">Errors</th>
            <th className="text-right px-4 py-2.5 whitespace-nowrap">Duration</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              aria-label={`Select the ${fmtRunDate(r.run_date)} run`}
              className={`${TABLE_ROW} ${TABLE_ROW_CLICKABLE}`}
              {...clickableRowProps(() => onSelectRun?.(r.id))}
            >
              <td className="px-4 py-3" data-label="Date">{fmtRunDate(r.run_date)}</td>
              <td className="px-4 py-3" data-label="Type">{r.run_type_display || prettifyCategory(r.run_type) || ''}</td>
              <td className="px-4 py-3 capitalize" data-label="Status">{r.status}</td>
              <td className="px-4 py-3 text-right tabular-nums" data-label="Calls">{r.total_api_calls ?? 0}</td>
              <td className={`px-4 py-2 text-right tabular-nums ${Number(r.total_errors || 0) > 0 ? 'text-red-600 dark:text-red-400' : ''}`} data-label="Errors">{r.total_errors ?? 0}</td>
              <td className={`px-4 py-2 text-right tabular-nums ${isAbsurdDuration(r.duration_seconds) ? 'text-red-600 dark:text-red-400' : ''}`} data-label="Duration">
                {isAbsurdDuration(r.duration_seconds) ? (
                  <span className="group relative inline-block cursor-help">
                    <span>{fmtDuration(r.duration_seconds)}</span>
                    <span
                      role="tooltip"
                      style={{ width: 260 }}
                      className="absolute right-0 bottom-full mb-2 pointer-events-none opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200 z-[9999]"
                    >
                      <span className="relative block bg-gray-900 text-white text-sm leading-relaxed rounded-lg px-4 py-3 shadow-2xl border border-white/20 whitespace-normal text-left font-normal normal-case">
                        Run duration exceeds 24h — likely a stuck or orphaned run, not the real run time.
                        <span className="absolute top-full right-1.5 -mt-px w-0 h-0 border-[6px] border-transparent border-t-gray-900" />
                      </span>
                    </span>
                  </span>
                ) : fmtDuration(r.duration_seconds)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function QueueHealth({ clientId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!clientId) return;
    getAimQueueHealth(clientId).then((resp) => { if (!cancelled) setData(resp?.data || null); }).catch(() => {});
    return () => { cancelled = true; };
  }, [clientId]);
  if (!data) return null;
  const { hex, recognised } = queueHealthColor(data.status);
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Queue Health (8B)</h3>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 mb-2">
        Live status of the AI Monitor job queue. Healthy means jobs are flowing; stalled or failed means runs may be delayed and the data on other tabs could be older than expected.
      </p>
      <div className="flex items-center gap-3 max-[499px]:flex-wrap">
        <span
          className={`inline-block w-3 h-3 rounded-full shrink-0 ${recognised ? '' : 'outline outline-1 outline-dashed outline-offset-1 outline-gray-400 dark:outline-gray-500'}`}
          style={{ backgroundColor: hex }}
          // A dashed outline marks a status the colour map has never seen, so a
          // future vocabulary change is visible rather than passing as `unknown`.
          title={recognised ? undefined : `Unrecognised queue status: ${data.status}`}
        />
        <span className="font-semibold text-gray-900 dark:text-white capitalize max-[499px]:flex-1">{data.status}</span>
        <span className="text-sm text-gray-500 dark:text-gray-400 max-[499px]:w-full">
          {data.pending} pending · {data.stalled} stalled · {data.failed} failed
        </span>
      </div>
    </div>
  );
}

/**
 * Responsive, flex-wrapping legend for the Cost Summary chart. Rendered as plain
 * HTML below the chart instead of Recharts' built-in <Legend>, which gets squeezed
 * into the fixed 240px ResponsiveContainer height and overlaps the x-axis date
 * labels once the 11 provider entries wrap on narrow viewports. Wrapping is driven
 * purely by CSS flex-wrap, so it reflows cleanly at any screen size.
 */
/**
 * Responsive, flex-wrapping legend for the Cost Summary chart. Rendered as plain
 * HTML below the chart instead of Recharts' built-in <Legend>, which gets squeezed
 * into the fixed ResponsiveContainer height and overlaps the x-axis date labels
 * once the 11 provider entries wrap on narrow viewports. Wrapping is driven purely
 * by CSS flex-wrap, so it reflows cleanly at any screen size.
 */
function CostLegend({ providers }) {
  if (!providers?.length) return null;
  return (
    <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 list-none p-0">
      {providers.map((p) => {
        const { hex, faded } = colorFor(p);
        return (
          <li key={p} className="flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300">
            <span
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: hex, opacity: faded ? 0.5 : 1 }}
            />
            {prettifyProviderLabel(p)}
          </li>
        );
      })}
    </ul>
  );
}

// One RPC call per charted run, so a pathological all-time range is capped. Set
// above the current run count (27) so nothing is dropped today; if it ever bites,
// the card says how many runs it left out rather than truncating silently.
const COST_RUN_BUDGET = 30;

function CostSummary({ clientId }) {
  const [runs, setRuns] = useState([]);
  const [costByRunId, setCostByRunId] = useState(() => new Map());
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!clientId) return undefined;
    setLoading(true);
    setErrored(false);
    // fn_get_aim_runs supplies the run identity — fn_get_aim_cost_summary's rows
    // carry no run_id or run_date, so the runs have to be enumerated first.
    getAimRuns(clientId)
      .then((resp) => { if (!cancelled) setRuns(resp?.data || []); })
      .catch(() => { if (!cancelled) { setRuns([]); setErrored(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  // AI Monitor is run-keyed, not date-keyed — the header date-range picker is
  // disabled on this route (Epic 2), so every run for the property is charted.
  // `limitCostRuns` still caps the fetch fan-out (one cost RPC per run) at
  // COST_RUN_BUDGET, newest-biased, so an all-time history can't stall the tab.
  const budgeted = useMemo(() => limitCostRuns(runs, COST_RUN_BUDGET), [runs]);
  const budgetedIdsKey = budgeted.runs.map((r) => r.id).join(',');

  // The per-provider stack needs one call per run. Verified 2026-07-29 that
  // historical runs do return the full split and that each run's provider costs
  // sum to its total — see lib/costSummary.
  useEffect(() => {
    let cancelled = false;
    if (!clientId || budgeted.runs.length === 0) { setCostByRunId(new Map()); return undefined; }
    Promise.all(budgeted.runs.map((r) => getAimCostSummary(clientId, r.id)
      .then((resp) => [r.id, resp?.data || []])
      .catch(() => [r.id, null])))
      .then((pairs) => {
        if (cancelled) return;
        // A failed run is left out of the map so buildCostSeries counts it as
        // missing, rather than charting it as a run that cost nothing.
        setCostByRunId(new Map(pairs.filter(([, v]) => Array.isArray(v) && v.length > 0)));
      });
    return () => { cancelled = true; };
  }, [clientId, budgetedIdsKey]); // eslint-disable-line

  const { chartData, providers, dateByKey, missingRuns } = useMemo(
    () => buildCostSeries(budgeted.runs, costByRunId),
    [budgeted.runs, costByRunId],
  );

  if (loading) return <AimLoading height={200} />;
  // Hide the card entirely when there's nothing meaningful to render (load
  // error, empty response, or empty-after-date-filter). Empty placeholders for
  // an internal cost metric clutter the page without adding value.
  if (errored || chartData.length === 0) return null;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Cost Summary (8C)</h3>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
        Total compute spend per recent run, broken down by provider. Useful for spotting an unexpectedly expensive run or a pricing change.
      </p>
      {/* Tilt the date labels (via Recharts angle, not the mobile-only CSS
          helper) and reserve vertical room so the spelled-out dates never
          overlap — the card simply grows taller instead of stacking labels. */}
      <div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} layout="horizontal" margin={{ top: 5, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid stroke="rgba(148,163,184,0.2)" />
            <XAxis
              dataKey="runKey"
              tick={{ fontSize: 11 }}
              tickFormatter={(k) => fmtRunDate(dateByKey[k])}
              interval={0}
              angle={-35}
              textAnchor="end"
              height={72}
            />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${Number(v).toFixed(2)}`} />
            <ThemedChartTooltip
              labelFormatter={(k) => fmtRunDate(dateByKey[k])}
              formatter={(v) => `$${Number(v).toFixed(2)}`}
            />
            {providers.map((p) => (
              <Bar key={p} dataKey={p} stackId="cost" name={prettifyProviderLabel(p)} fill={colorFor(p).hex} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <CostLegend providers={providers} />
      {(budgeted.dropped > 0 || missingRuns > 0) && (
        // Never let a shortfall pass as data: a run whose breakdown failed to
        // load, or one trimmed by the fetch budget, is named rather than shown
        // as a run that cost nothing.
        <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
          {budgeted.dropped > 0 && `Showing the ${COST_RUN_BUDGET} most recent runs in this range; ${budgeted.dropped} older ${budgeted.dropped === 1 ? 'run is' : 'runs are'} not charted. `}
          {missingRuns > 0 && `${missingRuns} ${missingRuns === 1 ? 'run’s' : 'runs’'} per-provider breakdown could not be loaded and ${missingRuns === 1 ? 'is' : 'are'} omitted.`}
        </p>
      )}
    </div>
  );
}

function ErrorsTable({ clientId, runId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    if (!clientId || !runId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    getAimErrors(clientId, runId)
      .then((resp) => { if (!cancelled) setRows(resp?.data || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, runId]);

  const { visibleRows, page, setPage, pageCount, totalItems } = usePagedRows(rows, 25);

  if (loading) return <AimLoading height={140} />;
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 p-4 text-sm text-emerald-700 dark:text-emerald-300">
        ✅ No errors in this run
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Errors (8D)</h3>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
          Recent provider call failures grouped by error type. A spike here usually maps to a provider outage or a credential / quota issue.
        </p>
      </div>
      <div className={`${TABLE_FRAME_FLUSH} responsive-card-table-wrapper`}>
      <table className="w-full text-sm text-gray-900 dark:text-gray-100 min-w-[560px] responsive-card-table">
        <thead className={TABLE_THEAD}>
          <tr>
            <th className="text-left px-4 py-2.5 whitespace-nowrap">Provider</th>
            <th className="text-left px-4 py-2.5 whitespace-nowrap">Message</th>
            <th className="text-right px-4 py-2.5 whitespace-nowrap">Count</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((r, i) => (
            <tr key={i} className={TABLE_ROW}>
              <td className="px-4 py-3" data-label="Provider">{prettifyProviderLabel(r.display_name) || '—'}</td>
              <td className="px-4 py-3 text-red-600 dark:text-red-400 truncate max-w-[540px]" title={r.error} data-label="Message">{r.error || '—'}</td>
              <td className="px-4 py-3 text-right tabular-nums" data-label="Count">{r.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div className="px-4 pb-3">
        <AimPagination page={page} pageCount={pageCount} onPage={setPage} totalItems={totalItems} label="error" />
      </div>
    </div>
  );
}

function DataCounts({ clientId }) {
  const { isAdmin } = useView();
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!clientId) return;
    getAimDataCounts(clientId).then((resp) => { if (!cancelled) setData(resp?.data || null); }).catch(() => {});
    return () => { cancelled = true; };
  }, [clientId]);

  if (!data) return null;
  // Total Spend is an internal cost metric — only Super Admin / Admin (roles
  // 0–1) should see it. Managers (2) and Clients (3) get the rest of the
  // inventory cards without the dollar figure (markup #71).
  const cards = [
    { label: 'Runs', value: data.runs },
    { label: 'Responses', value: data.responses },
    { label: 'Property Mentions', value: data.mentions_client },
    { label: 'Competitor Mentions', value: data.mentions_competitor },
    { label: 'Facts', value: data.facts },
    { label: 'Citations', value: data.citations },
    { label: 'SERP Organic Rows', value: data.serp_organic },
    {
      label: 'Multi-Turn Chains',
      value: data.multi_turn,
      tooltip: 'Number of multi-turn conversations recorded — prompts where the user follows up on an initial answer (e.g. "tell me more about that"), which the AI Monitor replays in sequence to test how each provider holds context.',
    },
    { label: 'Ground Truth Facts', value: data.ground_truth },
    ...(isAdmin ? [{ label: 'Total Spend', value: fmtCost(data.total_cost_usd) }] : []),
  ];
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Total Data Counts (8E)</h3>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
        Cumulative inventory across all runs — total responses analyzed, mentions captured, facts validated, etc. Use it to gauge how much data the dashboard above is computed from.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center gap-1">
              <span>{c.label}</span>
              {c.tooltip && (
                <InfoTooltip label={c.label} align="right" width={260}>
                  {c.tooltip}
                </InfoTooltip>
              )}
            </div>
            <div className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">
              {typeof c.value === 'number' ? c.value.toLocaleString() : (c.value ?? '—')}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SystemTab({ clientId, runId, onSelectRun }) {
  return (
    <div className="space-y-5">
      <QueueHealth clientId={clientId} />
      <RunOverview clientId={clientId} onSelectRun={onSelectRun} />
      <CostSummary clientId={clientId} />
      <ErrorsTable clientId={clientId} runId={runId} />
      <DataCounts clientId={clientId} />
    </div>
  );
}
